import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/InputDefault';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import IconProvider from '@/components/ui/IconProvider';
import { useToast } from '@/hooks/use-toast';
import { 
  Download, 
  Trash2, 
  Cloud, 
  Globe, 
  Lock, 
  Copy, 
  Check,
  RefreshCw,
  Edit2,
  X,
  Save
} from 'lucide-react';
import { useExportVersions, ExportVersion } from './useExportVersions';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ExportVersionsSectionProps {
  gameId: string;
}

export function ExportVersionsSection({ gameId }: ExportVersionsSectionProps) {
  const { toast } = useToast();
  const { 
    versions, 
    isLoading, 
    error, 
    fetchVersions, 
    deleteVersion, 
    updateVersion,
    downloadVersion 
  } = useExportVersions();
  
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (gameId) {
      fetchVersions(gameId);
    }
  }, [gameId, fetchVersions]);

  const handleDelete = async (versionId: string) => {
    const success = await deleteVersion(versionId);
    if (success) {
      toast({
        title: 'Version deleted',
        description: 'The export version has been deleted',
      });
    } else {
      toast({
        title: 'Delete failed',
        description: 'Failed to delete the export version',
        variant: 'destructive',
      });
    }
    setDeleteConfirmId(null);
  };

  const handleTogglePublic = async (version: ExportVersion) => {
    const result = await updateVersion(version.id, { is_public: !version.is_public });
    if (result) {
      toast({
        title: version.is_public ? 'Made private' : 'Made public',
        description: version.is_public 
          ? 'This version is no longer accessible via API' 
          : 'This version can now be accessed via API',
      });
    }
  };

  const handleStartEdit = (version: ExportVersion) => {
    setEditingId(version.id);
    setEditName(version.version_name);
  };

  const handleSaveEdit = async (versionId: string) => {
    if (!editName.trim()) {
      toast({
        title: 'Name required',
        description: 'Version name cannot be empty',
        variant: 'destructive',
      });
      return;
    }
    
    const result = await updateVersion(versionId, { version_name: editName.trim() });
    if (result) {
      toast({
        title: 'Version renamed',
        description: 'The version name has been updated',
      });
    }
    setEditingId(null);
  };

  const handleCopyUrl = (version: ExportVersion) => {
    if (version.download_url) {
      navigator.clipboard.writeText(version.download_url);
      setCopiedId(version.id);
      toast({
        title: 'URL copied',
        description: 'Download URL copied to clipboard',
      });
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (versions.length === 0 && !isLoading) {
    return null; // Don't show section if no versions
  }

  return (
    <div className="border border-border rounded p-6 space-y-4 bg-card">
      <div className="flex items-start gap-4">
        <Cloud size={24} className="text-primary mt-1" />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="heading-4">Saved Export Versions</h2>
              <p className="text-muted-foreground text-sm">
                Previously saved exports stored in the cloud
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fetchVersions(gameId)}
              disabled={isLoading}
              className="gap-2"
            >
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </Button>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded p-3 mb-4">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          {isLoading && versions.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw size={24} className="animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((version) => (
                <div 
                  key={version.id} 
                  className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Version Name */}
                      <div className="flex items-center gap-2 mb-1">
                        {editingId === version.id ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="h-7 text-sm"
                              autoFocus
                            />
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0"
                              onClick={() => handleSaveEdit(version.id)}
                            >
                              <Save size={14} />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0"
                              onClick={() => setEditingId(null)}
                            >
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <span className="font-medium truncate">{version.version_name}</span>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
                              onClick={() => handleStartEdit(version)}
                            >
                              <Edit2 size={12} />
                            </Button>
                          </>
                        )}
                      </div>

                      {/* Metadata Row */}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="capitalize bg-muted px-2 py-0.5 rounded">
                          {version.platform}
                        </span>
                        <span>{formatFileSize(version.file_size)}</span>
                        <span>{formatDate(version.created_at)}</span>
                        {version.download_count > 0 && (
                          <span className="flex items-center gap-1">
                            <Download size={10} />
                            {version.download_count}
                          </span>
                        )}
                        {version.is_public ? (
                          <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <Globe size={10} />
                            Public
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Lock size={10} />
                            Private
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        onClick={() => downloadVersion(version)}
                      >
                        <Download size={14} />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => handleCopyUrl(version)}
                        title="Copy download URL"
                      >
                        {copiedId === version.id ? (
                          <Check size={14} className="text-green-500" />
                        ) : (
                          <Copy size={14} />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        onClick={() => setDeleteConfirmId(version.id)}
                        title="Delete version"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>

                  {/* Public Toggle Row */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <div className="flex items-center gap-2">
                      <Globe size={14} className="text-muted-foreground" />
                      <Label htmlFor={`public-${version.id}`} className="text-xs text-muted-foreground">
                        API Access
                      </Label>
                    </div>
                    <Switch
                      id={`public-${version.id}`}
                      checked={version.is_public}
                      onCheckedChange={() => handleTogglePublic(version)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Export Version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the export version from cloud storage.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
